import { useCallback, useEffect, useRef } from 'react';
import type { MouseEventHandler, PointerEventHandler } from 'react';

const MOVE_THRESHOLD_SQ = 8 * 8;

type Session = {
  cleanup: () => void;
};

/**
 * Preview paste slots use `onClick` to select; panning nested SVGs still synthesizes `click`,
 * which incorrectly selects after a drag. Track pointer movement and suppress one click after a drag.
 */
export function useTapClickSelectSkippingDrag(onSelect?: () => void): {
  onPointerDownCapture: PointerEventHandler<HTMLElement>;
  onClickSkipDrag: MouseEventHandler<HTMLElement>;
} {
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const sessionRef = useRef<Session | null>(null);
  const suppressNextClickRef = useRef(false);

  useEffect(
    () => () => {
      sessionRef.current?.cleanup();
      sessionRef.current = null;
    },
    [],
  );

  const onPointerDownCapture = useCallback<PointerEventHandler<HTMLElement>>((e) => {
    if (!onSelectRef.current || e.button !== 0) return;

    sessionRef.current?.cleanup();

    const startX = e.clientX;
    const startY = e.clientY;
    const pointerId = e.pointerId;
    let dragged = false;

    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (dx * dx + dy * dy > MOVE_THRESHOLD_SQ) dragged = true;
    };

    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      suppressNextClickRef.current = dragged;
      sessionRef.current = null;
    };

    const session: Session = {
      cleanup: () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
      },
    };
    sessionRef.current = session;

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  }, []);

  const onClickSkipDrag = useCallback<MouseEventHandler<HTMLElement>>((_e) => {
    if (!onSelectRef.current) return;
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      return;
    }
    onSelectRef.current();
  }, []);

  return { onPointerDownCapture, onClickSkipDrag };
}
